import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from 'firebase/auth';
import { onAuthChange, setupUserPresence, getOnlineUsers, logoutAdmin, auth, type OnlineUser } from '../services/firebase';
import { Lab, UserProfile } from '../types/types';
import { getUserProfile } from '../services/userService';
import { getLab } from '../services/labService';
import { setActiveThemeId } from '../utils/pdfThemes';

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  currentLab: Lab | null;
  loading: boolean;
  onlineUsers: OnlineUser[];
  isOnline: boolean;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  userProfile: null,
  currentLab: null,
  loading: true,
  onlineUsers: [],
  isOnline: navigator.onLine,
  logout: async () => { },
  refreshUser: async () => { }
});

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [currentLab, setCurrentLab] = useState<Lab | null>(null);
  const [loading, setLoading] = useState(true);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    // Monitor authentication state
    console.log('Setting up auth state listener...');
    const unsubscribe = onAuthChange(async (user) => {
      console.log('Auth state changed:', user ? 'User logged in' : 'User logged out');
      console.log('User details:', user);

      if (user) {
        setLoading(true);
      }

      setUser(user);

      if (user) {
        // Fetch User Profile
        try {
          const profile = await getUserProfile(user.uid);
          setUserProfile(profile);

          if (profile && profile.labId) {
            console.log('User belongs to lab:', profile.labId);
            const lab = await getLab(profile.labId);
            setCurrentLab(lab);
            if (lab?.activeThemeId) {
              setActiveThemeId(lab.activeThemeId);
            }
          } else {
            console.log('User has no lab associated');
            setCurrentLab(null);
          }
        } catch (error) {
          console.error('Error fetching user profile or lab:', error);
          setCurrentLab(null);
          setUserProfile(null);
        }

        console.log('Setting up user presence for:', user.email);
        try {
          setupUserPresence(user);
        } catch (error) {
          console.error('Error setting up user presence:', error);
        }
      } else {
        setCurrentLab(null);
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    // Monitor online users
    let unsubscribeUsers: (() => void) | undefined;

    if (user) {
      unsubscribeUsers = getOnlineUsers((users) => {
        setOnlineUsers(users);
      });
    }

    return () => {
      if (unsubscribeUsers) {
        unsubscribeUsers();
      }
    };
  }, [user]);

  useEffect(() => {
    // Monitor network status
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const logout = async (): Promise<void> => {
    try {
      await logoutAdmin();
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    userProfile,
    currentLab,
    loading,
    onlineUsers,
    isOnline,
    logout,
    refreshUser: async () => {
      if (user) {
        await user.reload();
        // Force state update by creating a new object reference if properties changed, 
        // but Firebase Auth user object is mutable so we just set it again to trigger re-renders
        // or better, we can fetch the current user from auth.currentUser
        const refreshedUser = auth.currentUser;
        if (refreshedUser) {
          setUser({ ...refreshedUser });
        }
      }
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};